// ============================================================================
// AdminReportsPanel — cross-event budget rollups (Task #8).
// ============================================================================
// Sidebar entry: #admin/reports. UIF only.
//
// One panel with:
//   * Date range filter + draft toggle
//   * 4 KPI cards (revenue, expense, net expected, net actual)
//   * By-event table
//   * By-category breakdown (expense + income split)
//   * By-funding-source breakdown
//   * CSV export per table
//   * Print button (browser print-to-PDF)
//
// CSV export is client-side: build the rows, blob it, trigger download. No
// server round-trip needed — the backend already returns structured data.
// PDF is browser-print rather than a Lambda PDF lib to keep the bundle thin.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, Printer, Download, RefreshCw } from 'lucide-react';
import { api, type BudgetReport } from '../api';

interface Props {
  token: string;
}

function fmtMoney(s: string | number): string {
  const n = typeof s === 'string' ? Number(s) : s;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Returns YYYY-MM-DD for use in date inputs.
function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function downloadCsv(rows: Array<Record<string, any>>, filename: string) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const escape = (v: any): string => {
    const s = v == null ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminReportsPanel({ token }: Props) {
  // Default range = year-to-date.
  const today = useMemo(() => new Date(), []);
  const yearStart = useMemo(() => new Date(today.getFullYear(), 0, 1), [today]);

  const [from, setFrom] = useState(isoDay(yearStart));
  const [to, setTo] = useState(isoDay(today));
  const [includeDrafts, setIncludeDrafts] = useState(false);

  const [report, setReport] = useState<BudgetReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.budgetReport(token, { from, to, includeDrafts })
      .then(setReport)
      .catch((err) => setError(err?.message || 'Could not load report.'))
      .finally(() => setLoading(false));
  }, [token, from, to, includeDrafts]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Cross-event budget rollups. Filter by date range, then export any table to CSV or print the whole page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 print:hidden"
        >
          <Printer className="h-4 w-4" />
          Print / save as PDF
        </button>
      </div>

      {/* Filter bar — hidden on print. */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3 print:hidden">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={includeDrafts}
            onChange={(e) => setIncludeDrafts(e.target.checked)}
          />
          Include drafts
        </label>
        <div className="flex-1" />
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
        >
          <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {loading && !report && (
        <div className="p-6 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Building report…
        </div>
      )}

      {report && (
        <>
          {/* Report header (print-friendly) */}
          <div className="hidden print:block text-sm text-gray-700">
            <div><strong>UIF Budget Report</strong></div>
            <div>Range: {fmtDate(report.range.from)} → {fmtDate(report.range.to)}</div>
            <div>{report.range.event_count} events · {report.range.line_count} line items</div>
            {report.range.include_drafts && <div>Includes draft events.</div>}
            <hr className="my-2" />
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Expected expense"
              value={fmtMoney(report.totals.expected_expense)}
              sub={`${report.range.event_count} events`}
              tone="expense"
            />
            <KpiCard
              label="Actual expense"
              value={fmtMoney(report.totals.actual_expense)}
              sub={`${report.range.line_count} line items`}
              tone="expense"
            />
            <KpiCard
              label="Expected income"
              value={fmtMoney(report.totals.expected_income)}
              sub="grants + fees"
              tone="income"
            />
            <KpiCard
              label="Net actual"
              value={fmtMoney(report.totals.net_actual)}
              sub={`vs. ${fmtMoney(report.totals.net_expected)} expected`}
              tone="net"
            />
          </div>

          {/* By event */}
          <ReportSection
            title="By event"
            onExport={() => downloadCsv(
              report.by_event.map((r) => ({
                event_id:         r.calendar_entry_id,
                title:            r.title,
                start_date:       r.start_date,
                audience:         r.audience,
                is_draft:         r.is_draft,
                expected_expense: r.expected_expense,
                actual_expense:   r.actual_expense,
                expected_income:  r.expected_income,
                actual_income:    r.actual_income,
                net_expected:     r.net_expected,
                net_actual:       r.net_actual,
                line_count:       r.line_count,
              })),
              `uif-budget-by-event-${from}_to_${to}.csv`,
            )}
          >
            {report.by_event.length === 0 ? (
              <Empty message="No events in this range." />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2">Event</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2 text-right">Exp. expense</th>
                    <th className="px-3 py-2 text-right">Act. expense</th>
                    <th className="px-3 py-2 text-right">Net actual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.by_event.map((r) => (
                    <tr key={r.calendar_entry_id} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <a className="text-uif-green-dark hover:underline" href={`#admin/budgets/${r.calendar_entry_id}`}>
                          {r.title}
                        </a>
                        {r.is_draft && <span className="ml-2 text-[10px] uppercase text-amber-600">draft</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{fmtDate(r.start_date)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(r.expected_expense)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(r.actual_expense)}</td>
                      <td className={'px-3 py-2 text-right font-medium ' + (Number(r.net_actual) > 0 ? 'text-red-700' : 'text-emerald-700')}>
                        {fmtMoney(r.net_actual)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportSection>

          {/* By category */}
          <ReportSection
            title="By category"
            onExport={() => downloadCsv(
              report.by_category.map((r) => ({
                category:  r.category,
                kind:      r.is_income ? 'income' : 'expense',
                expected:  r.expected,
                actual:    r.actual,
                variance:  (Number(r.actual) - Number(r.expected)).toFixed(2),
                line_count: r.line_count,
              })),
              `uif-budget-by-category-${from}_to_${to}.csv`,
            )}
          >
            {report.by_category.length === 0 ? (
              <Empty message="No budget lines in this range." />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2">Category</th>
                    <th className="px-3 py-2">Kind</th>
                    <th className="px-3 py-2 text-right">Expected</th>
                    <th className="px-3 py-2 text-right">Actual</th>
                    <th className="px-3 py-2 text-right">Variance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.by_category.map((r, i) => {
                    const variance = Number(r.actual) - Number(r.expected);
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-gray-900 capitalize">{r.category}</td>
                        <td className="px-3 py-2 text-xs uppercase tracking-wider text-gray-500">{r.is_income ? 'income' : 'expense'}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(r.expected)}</td>
                        <td className="px-3 py-2 text-right">{fmtMoney(r.actual)}</td>
                        <td className={'px-3 py-2 text-right font-medium ' + (variance > 0 ? 'text-red-700' : variance < 0 ? 'text-emerald-700' : 'text-gray-500')}>
                          {variance > 0 ? '+' : ''}{fmtMoney(variance)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </ReportSection>

          {/* By funding source */}
          <ReportSection
            title="By funding source (expense allocations)"
            onExport={() => downloadCsv(
              report.by_funding.map((r) => ({
                funding_source: r.funding_source,
                expected: r.expected,
                actual:   r.actual,
              })),
              `uif-budget-by-funding-${from}_to_${to}.csv`,
            )}
          >
            {report.by_funding.length === 0 ? (
              <Empty message="No funding splits configured for expenses in this range." />
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2">Funding source</th>
                    <th className="px-3 py-2 text-right">Expected</th>
                    <th className="px-3 py-2 text-right">Actual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.by_funding.map((r) => (
                    <tr key={r.funding_source} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">{r.funding_source}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(r.expected)}</td>
                      <td className="px-3 py-2 text-right">{fmtMoney(r.actual)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportSection>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: 'expense' | 'income' | 'net' }) {
  const ring =
    tone === 'expense' ? 'border-red-100 bg-red-50/50'
    : tone === 'income' ? 'border-emerald-100 bg-emerald-50/50'
    : 'border-gray-200 bg-white';
  return (
    <div className={'rounded-lg border p-3 ' + ring}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
    </div>
  );
}

function ReportSection({ title, onExport, children }: { title: string; onExport: () => void; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <header className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
        <button
          type="button"
          onClick={onExport}
          className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 print:hidden"
          title="Download CSV"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
      </header>
      <div className="overflow-x-auto">
        {children}
      </div>
    </section>
  );
}

function Empty({ message }: { message: string }) {
  return <div className="px-3 py-6 text-sm text-gray-500 italic text-center">{message}</div>;
}
